import type { PermissionKey } from '@safetyhub/shared';

/**
 * 사이드바·더보기 내비의 표시 조건 — **순수 함수**(`Layout.tsx`가 유일한 소비처).
 *
 * ## 왜 별도 모듈인가
 *
 * `Layout`은 렌더에 스토어 5개·react-query·router가 필요해 spec에서 띄우기 어렵다. 그런데 여기서
 * 가리는 것은 조직 관리 메뉴 전부(`/members`·`/settings/*`)라, 축이 조용히 바뀌면 **메뉴가 통째로
 * 사라지거나 없어야 할 사람에게 열린다**. 판정만 순수 함수로 빼서 그 축을 spec이 직접 잰다.
 *
 * ## 규칙: 내비는 그 항목이 가리키는 페이지 게이트의 **거울**이다
 *
 * 이 규칙은 P6-b부터 지금까지 그대로다 — 바뀌는 것은 **거울에 비치는 축**뿐이다.
 * 한쪽만 바꾸면 **둘 중 하나**가 된다. 둘 다 결함이고, 방향이 반대다:
 *
 * | 어긋난 방향 | 증상 |
 * |---|---|
 * | 내비가 페이지보다 **넓다** | 메뉴는 보이는데 들어가면 `/`로 튕긴다 — **막다른 길** |
 * | 내비가 페이지보다 **좁다** | 권한은 있는데 **메뉴가 없다** — 조직이 키를 켜 줘도 아무 일도 안 일어나는 **dead grant** |
 *
 * ⚠️ P6-b 시점의 이 doc은 **넓은 쪽만** 경고했고, 그래서 P7-g에서 실제로 새어 나간 쪽(좁은 쪽)을 막지
 * 못했다. 두 방향을 함께 적어 두는 이유다.
 *
 * ## 지금 각 항목의 축 (P7-g, 2026-09-25)
 *
 * 페이지 진입 게이트가 권한 key로 옮겨간 셋은 내비도 **같은 키**를 읽는다(`anyPermission`):
 *   - `/settings/roles` → `org.roles.manage`
 *   - `/settings/organization` → `org.settings.manage`
 *   - `/members` → `member.manage ∥ member.invite`(그 화면에서 할 수 있는 일이 하나라도 있나)
 *
 * 소유자 **성질**로 남는 항목:
 *   - `/settings/groups` — 서버도 `isOwnerMember`이고 대응 권한 키가 없다
 *   - `/settings/approval` — in-page 게이트가 아예 없어 거울로 삼을 대상이 없다(현행 보존)
 *   - `/organization-info`(`memberOnly`) — 「소유자가 **아닌** 사람」이라 권한 key로 표현되지 않는다
 *
 * ## 부모 항목의 링크 목적지
 *
 * `조직 관리`는 링크이면서 자식(`역할·선임` 등)의 컨테이너다. 그래서 **보일 조건**과 **갈 곳**이 다르다:
 * 보이는 조건은 자기 페이지 키 ∥ 자식들의 키(합집합)이고, `org.roles.manage`만 가진 사람에게 부모
 * 링크를 자기 페이지로 두면 클릭이 `/`로 튕긴다(위 표의 「막다른 길」). `resolveNavItemPath`가 그 경우
 * 목적지를 **첫 가시 자식**으로 돌린다.
 *
 * 소유자 판정은 `isOwner` **필드만** 본다(옛 `role === 'OWNER'` 비교 없음 —
 * `utils/siteAccess.ts`·`hooks/usePermissions.ts` doc와 같은 규약).
 *
 * ## 피처 플래그 칸 — `flagKey` × `flagGates`
 *
 * admin `Layout.tsx`의 `flagKey`와 같은 «항목 쪽» 모양이지만, **판정은 여기서 하지 않고 주입받는다.**
 * 플래그 뒤 메뉴의 가시성이 「보는 사람에게 그 플래그가 켜졌나」 하나로 접히지 않기 때문이다 —
 * `legal-obligation`은 「보는 사람의 조직이 켜졌거나, **켜진 수검업체의 현장**을 하나라도 관리하면」
 * 보이고(결정 `2026-10-23-legal-document-obligation-management.md` ⓔ *고객 화면 입구* #4) 그 판정은
 * 서버 `GET /legal-documents/visibility`가 한다. `/feature-flags/me`로 평가하면 수검업체 축이 빠져
 * **켜진 현장을 관리하는 기관에게 메뉴가 없다**(위 표의 「좁다」).
 *
 * 그래서 `flagGates`는 **플래그 키 → 그 메뉴를 열지**의 표이고, 채우는 쪽(`Layout`)이 키마다
 * 출처를 고른다. ⛔ **키가 표에 없으면 숨긴다** — 로딩 중·조회 실패·배선 누락이 전부 그 상태이고,
 * 「모르면 보여 준다」로 두면 플래그가 꺼진 조직에 메뉴가 새어 나간다(그 페이지도 같은 표로 거른다).
 */

/** 판정에 쓰는 메뉴 항목 조각. `Layout`의 `MenuItem`이 구조적으로 만족한다. */
export interface NavVisibilityItem {
  /** 소유자만 볼 수 있는 항목 — 권한 key로 표현되지 않는 판정에만 쓴다(위 doc) */
  ownerOnly?: boolean;
  /** 소유자가 **아닌** 멤버만 볼 수 있는 항목 (조직 유형 무관 — WORKSPACE 팀 멤버 포함) */
  memberOnly?: boolean;
  /** 비즈니스 조직(WORKSPACE가 아닌 조직)에서만 볼 수 있는 항목 */
  businessOnly?: boolean;
  /**
   * 권한 key 축 — 이 중 **하나라도** 가지면 보인다(합집합, deny 없음).
   * 그 항목이 가리키는 페이지의 진입 게이트와 같은 키여야 한다(위 doc의 거울 규칙).
   * 자식을 품은 항목이면 자기 페이지 키 + 자식들의 키를 모두 담는다.
   */
  anyPermission?: readonly PermissionKey[];
  /**
   * 이 항목의 **자기 페이지**가 요구하는 키. 자식을 품은 항목에서만 뜻이 있다 —
   * 없으면 링크 목적지를 첫 가시 자식으로 돌린다(`resolveNavItemPath`).
   */
  selfPermission?: PermissionKey;
  /**
   * 피처 플래그 뒤 항목 — `flagGates[flagKey] === true`일 때만 보인다(위 doc *피처 플래그 칸*).
   * 다른 조건과 AND다.
   */
  flagKey?: string;
}

/** 플래그 키 → 그 플래그 뒤 메뉴를 열지. 키 부재는 «숨김»이다(위 doc). */
export type NavFlagGates = Readonly<Record<string, boolean>>;

/** 판정 축. `resolveNavVisibilityContext`가 현재 멤버십에서 뽑는다. */
export interface NavVisibilityContext {
  isOwner: boolean;
  isBusinessOrg: boolean;
  /**
   * 그 조직에서 내가 가진 권한 key 합집합 — `/auth/me`의 `currentMembership.permissions`
   * (서버 `resolvePermissions()`; 소유자면 전체 키가 실린다).
   */
  permissions: readonly string[];
  /** `flagKey` 항목의 가시성 표 — 채우는 쪽이 키마다 출처를 고른다(위 doc) */
  flagGates: NavFlagGates;
}

/** 이 판정에 필요한 멤버십 조각만. 옛 `role`은 **읽지 않는다**. */
export interface NavVisibilityMembership {
  /**
   * 소유자 **성질** — `/auth/me`의 `currentMembership.isOwner`(서버 `resolvePermissions().isOwner`).
   * 옛 `role` 컬럼과 독립이다(소유자 역할 «배정»에서 온다).
   */
  isOwner?: boolean;
  /** 권한 key 합집합. 부재(옛 응답·로딩 중 스냅샷)는 **빈 배열**로 떨어뜨린다 — 「모르면 보여 준다」가 되지 않게. */
  permissions?: string[];
  company?: { category?: string | null };
}

/**
 * 현재 멤버십 → 판정 축. 멤버십이 없으면 소유자도 비즈니스도 아니고 권한도 없다(관리 메뉴가 통째로 숨는다).
 * `flagGates`를 안 주면 빈 표 — 플래그 뒤 항목이 전부 숨는다.
 */
export function resolveNavVisibilityContext(
  membership: NavVisibilityMembership | null | undefined,
  flagGates: NavFlagGates = {},
): NavVisibilityContext {
  return {
    isOwner: membership?.isOwner === true,
    isBusinessOrg: membership ? membership.company?.category !== 'WORKSPACE' : false,
    permissions: Array.isArray(membership?.permissions) ? membership.permissions : [],
    flagGates,
  };
}

/** 이 항목을 내비에 띄울까. 조건은 AND — 하나라도 어긋나면 숨긴다. */
export function isNavItemVisible(
  item: NavVisibilityItem,
  { isOwner, isBusinessOrg, permissions, flagGates }: NavVisibilityContext,
): boolean {
  if (item.businessOnly && !isBusinessOrg) return false;
  if (item.ownerOnly && !isOwner) return false;
  if (item.memberOnly && isOwner) return false;
  if (item.anyPermission && !item.anyPermission.some((key) => permissions.includes(key))) {
    return false;
  }
  // `=== true`로 묻는다 — 키 부재(undefined)를 «숨김»으로 떨어뜨리는 것이 이 칸의 계약이다(위 doc)
  if (item.flagKey !== undefined && flagGates[item.flagKey] !== true) return false;
  return true;
}

/**
 * 이 항목을 눌렀을 때 **갈 곳**.
 *
 * 자기 페이지에 못 들어가는데 자식 때문에 보이는 부모를 자기 `path`로 두면 클릭이 `/`로 튕긴다.
 * 그럴 때 첫 가시 자식으로 돌린다 — 가시 자식은 이미 `isNavItemVisible`을 통과한 것들이라
 * 목적지가 반드시 열린다.
 *
 * `selfPermission`이 없으면(대부분) 자기 페이지는 키를 묻지 않으므로 그대로 자기 `path`다.
 * 가시 자식이 하나도 없으면 그 부모 자체가 안 보이므로 이 함수의 결과는 쓰이지 않는다(방어적으로 자기 path).
 */
export function resolveNavItemPath(
  item: NavVisibilityItem & { path: string },
  { permissions }: NavVisibilityContext,
  visibleChildren: readonly { path: string }[],
): string {
  if (!item.selfPermission) return item.path;
  if (permissions.includes(item.selfPermission)) return item.path;
  return visibleChildren[0]?.path ?? item.path;
}
